import { bool, string, object } from 'prop-types';
import React, { useState, useEffect } from 'react';
import { QueryRenderer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';

import MessageBannerCarousel from './MessageBannerCarousel';
import {
    isPreview,
    getPreview,
    getIsFirstTimeVisitor,
    getPreviewVariables,
} from './messageHelpers';

import styles from './MessageBanner-style.scss';

/**
 * Messages for marketing banner can come from multiple sources and have the following precedence:
 * (1) Preview messages that pulled from a stringfied object on a URL query param
 * (2) Preview messages that are fetched with userType and/or setId from the URL
 * (3) Registered user messages that are fetched with userId
 * (4) Unregistered user messages that are fetched if user is not logged in
 */
function MessageBanner(props) {
    const [variables, setVariables] = useState({
        fireMessageBannerRefetch: false,
        isFirstTimeVisitor: false,
        hasRegisteredUserMessages: false,
        hasUnregisteredUserMessages: false,
        hasPreviewVariables: false,
        previewSetId: '',
        previewUserType: '',
    });
    const [previewMessagesFromUrl, setPreviewMessagesFromUrl] = useState(null);
    useEffect(() => {
        const { messages: previewMessages } = getPreview();
        if (previewMessages) {
            setPreviewMessagesFromUrl(previewMessages);
        }
    }, []);
    const { isClient, hasRootPendingRefetch, userId, hasUserId } = props;
    useEffect(() => {
        if (
            !variables.fireMessageBannerRefetch &&
            isClient &&
            !previewMessagesFromUrl &&
            !hasRootPendingRefetch
        ) {
            let nextVariables = {
                ...variables,
                userId,
                fireMessageBannerRefetch: true,
                isFirstTimeVisitor: !hasUserId && getIsFirstTimeVisitor(),
                hasRegisteredUserMessages: hasUserId,
                hasUnregisteredUserMessages: !hasUserId,
                hasPreviewVariables: false,
                previewSetId: '',
                previewUserType: '',
            };

            const { userType: previewUserType, setId: previewSetId } = getPreviewVariables();

            if (previewUserType && previewSetId) {
                nextVariables = {
                    ...nextVariables,
                    hasPreviewVariables: true,
                    previewSetId,
                    previewUserType,
                };
            }
            setVariables(nextVariables);
        }
    }, [hasRootPendingRefetch, isClient, variables, previewMessagesFromUrl, userId, hasUserId]);

    const classes = classnames({
        [styles.bannerContainer]: true,
        [styles.isTrade]: props.isUserTypeTrade,
        [styles.isBelow]: props.isBelow,
    });

    if (!isClient || (!variables.fireMessageBannerRefetch && !previewMessagesFromUrl)) {
        return <div className={classes} />;
    }

    const { isMultiLine } = props;

    if (previewMessagesFromUrl) {
        return (
            <div className={classes}>
                <MessageBannerCarousel
                    messages={previewMessagesFromUrl}
                    showAllMessages
                    isMultiLine={isMultiLine}
                />
            </div>
        );
    }

    return (
        <QueryRenderer
            environment={props.relayEnvironment}
            variables={variables}
            query={graphql`
                query MessageBannerQuery(
                    $userId: String!
                    $hasRegisteredUserMessages: Boolean!
                    $hasUnregisteredUserMessages: Boolean!
                    $isFirstTimeVisitor: Boolean!
                    $hasPreviewVariables: Boolean!
                    $previewSetId: String!
                    $previewUserType: String!
                ) {
                    viewer {
                        user(userId: $userId) @include(if: $hasRegisteredUserMessages) {
                            registeredUserMessages: bannerMessages {
                                ...MessageBannerCarousel_messages
                            }
                        }
                        unregisteredUserMessages: messageSet(
                            isFirstTimeVisitor: $isFirstTimeVisitor
                        ) @include(if: $hasUnregisteredUserMessages) {
                            ...MessageBannerCarousel_messages
                        }
                        previewMessages: messageSet(
                            setId: $previewSetId
                            userType: $previewUserType
                        ) @include(if: $hasPreviewVariables) {
                            ...MessageBannerCarousel_messages
                        }
                    }
                }
            `}
            render={({ error, props: renderProps }) => {
                if (error) {
                    return null;
                } else if (!renderProps) {
                    return <div className={classes} />;
                }

                const { viewer } = renderProps;
                let messages = viewer?.previewMessages || null;

                if (!isPreview() && viewer?.user?.registeredUserMessages) {
                    messages = viewer.user.registeredUserMessages;
                }
                if (!isPreview() && viewer?.unregisteredUserMessages) {
                    messages = viewer.unregisteredUserMessages;
                }

                return (
                    <div className={classes}>
                        <MessageBannerCarousel
                            messages={messages}
                            showAllMessages={false}
                            isMultiLine={isMultiLine}
                        />
                    </div>
                );
            }}
        />
    );
}

MessageBanner.propTypes = {
    hasRootPendingRefetch: bool,
    hasUserId: bool,
    isBelow: bool,
    isMultiLine: bool,
    isClient: bool,
    isUserTypeTrade: bool,
    userId: string,
    relayEnvironment: object,
};

const mapStateToProps = ({ header }) => {
    const { isUserTypeTrade } = header;

    return {
        isUserTypeTrade,
    };
};

export default connect(mapStateToProps)(MessageBanner);
